/**
 * Personas section (inside `CapabilityDrawer`) — per-persona (credential
 * template) verdicts for this capability: what each persona can/cannot/
 * limited-do here. Shows every persona defined on the property (Test Users
 * tab), merged with any recorded verdict, and lets a human set/edit/remove
 * one. Renders its own `CollapsibleSection` (rather than delegating that to
 * the drawer) since the verdict count needed for the section's badge is only
 * known once this component's own queries resolve.
 *
 * `limited` always carries a note — the API rejects a bare `limited` with
 * `PRODUCT_MAP_PERSONA_NOTE_REQUIRED`, so this blocks the save client-side
 * with an inline message instead of firing a request known to fail.
 *
 * Removing a verdict tombstones it server-side (`suppressedAt`) rather than
 * deleting the row — an agent will never resurrect a human's removal.
 *
 * @module features/product-map/components/CapabilityPersonasSection
 */

import { useState } from 'react';
import { Trash } from '@phosphor-icons/react';
import {
  Badge,
  CollapsibleSection,
  EditableBadge,
  Flex,
  InlineEditableText,
  ListCard,
  LoadingOrbit,
  Text,
} from '@chatsdet/ui';
import { useCredentialTemplates, type CredentialTemplate } from '../../../hooks/useCredentialTemplates';
import {
  useCapabilityPersonas,
  useRemoveCapabilityPersona,
  useSetCapabilityPersona,
  PERSONA_ACCESS_OPTIONS,
  PERSONA_ACCESS_VARIANT,
  type CapabilityPersonaAccess,
  type CapabilityPersonaView,
} from '../useCapabilityPersonas';

type Props = {
  capabilityId: string;
  propertyId: string;
};

export function CapabilityPersonasSection({ capabilityId, propertyId }: Props) {
  const templatesQuery = useCredentialTemplates(propertyId);
  const personasQuery = useCapabilityPersonas(capabilityId);
  const setPersona = useSetCapabilityPersona(capabilityId);
  const removePersona = useRemoveCapabilityPersona(capabilityId);

  const isLoading = templatesQuery.isLoading || personasQuery.isLoading;
  const templates = templatesQuery.data ?? [];
  const verdicts = personasQuery.data ?? [];
  const verdictByTemplateId = new Map(verdicts.map((v) => [v.credentialTemplateId, v] as const));

  return (
    <CollapsibleSection label="Personas" count={verdicts.length} defaultExpanded={templates.length > 0}>
      {isLoading ? (
        <div className="py-2">
          <LoadingOrbit size="sm" />
        </div>
      ) : templates.length === 0 ? (
        <Text size="sm" className="text-muted-foreground">
          No personas defined for this property yet — add them in Test Users.
        </Text>
      ) : (
        <ListCard>
          {templates.map((template) => (
            <PersonaRow
              key={template.id}
              template={template}
              verdict={verdictByTemplateId.get(template.id)}
              onSet={(input) => setPersona.mutate({ templateId: template.id, ...input })}
              onRemove={() => removePersona.mutate(template.id)}
              isSaving={setPersona.isPending}
              isRemoving={removePersona.isPending}
            />
          ))}
        </ListCard>
      )}
    </CollapsibleSection>
  );
}

function PersonaRow({
  template,
  verdict,
  onSet,
  onRemove,
  isSaving,
  isRemoving,
}: {
  template: CredentialTemplate;
  verdict: CapabilityPersonaView | undefined;
  onSet: (input: { access: CapabilityPersonaAccess; note?: string | null }) => void;
  onRemove: () => void;
  isSaving: boolean;
  isRemoving: boolean;
}) {
  // A selection made but not yet saved — only populated when the user picks
  // "Limited" with no note on record yet, so the note field can gather the
  // required note before the request fires.
  const [pendingAccess, setPendingAccess] = useState<CapabilityPersonaAccess | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);

  const access = pendingAccess ?? verdict?.access ?? null;
  const note = verdict?.note ?? '';

  const handleAccessChange = (next: CapabilityPersonaAccess) => {
    setNoteError(null);
    if (next !== 'limited' || verdict?.note) {
      setPendingAccess(null);
      onSet({ access: next, note: verdict?.note ?? null });
      return;
    }
    // "Limited" with no note yet — hold the selection and wait for the note.
    setPendingAccess('limited');
  };

  const handleNoteSave = (nextNote: string) => {
    if (!access) return;
    if (access === 'limited' && !nextNote.trim()) {
      setNoteError('A note is required for a limited verdict.');
      return;
    }
    setNoteError(null);
    setPendingAccess(null);
    onSet({ access, note: nextNote || null });
  };

  return (
    <div className="space-y-1 px-4 py-3">
      <Flex align="center" gap="sm">
        <Text size="sm" weight="medium" className="min-w-0 flex-1 text-pretty">
          {template.name}
        </Text>
        {verdict && (
          <Badge size="sm" variant={verdict.source === 'user' ? 'secondary' : 'outline'}>
            {verdict.source === 'user' ? 'Human' : 'Agent'}
          </Badge>
        )}
        <EditableBadge<CapabilityPersonaAccess | 'unset'>
          value={access ?? 'unset'}
          options={PERSONA_ACCESS_OPTIONS}
          onChange={(next) => handleAccessChange(next as CapabilityPersonaAccess)}
          variant={access ? PERSONA_ACCESS_VARIANT[access] : 'outline'}
          formatLabel={(v) => (v === 'unset' ? 'Set…' : (PERSONA_ACCESS_OPTIONS.find((o) => o.id === v)?.label ?? v))}
          size="sm"
          isLoading={isSaving}
          aria-label={`Access for ${template.name}`}
        />
        {verdict && (
          <button
            type="button"
            onClick={onRemove}
            disabled={isRemoving}
            aria-label={`Remove verdict for ${template.name}`}
            className="shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <Trash size={14} />
          </button>
        )}
      </Flex>
      {access && (
        <InlineEditableText
          value={note}
          onSave={handleNoteSave}
          ariaLabel={`Note for ${template.name}`}
          placeholder={access === 'limited' ? 'What is limited? (required)' : 'Add a note…'}
          // Always allow committing empty so `handleNoteSave` sees the empty
          // value and can show the required-note message — `allowEmpty:
          // false` would make the field silently revert instead.
          allowEmpty
          className="text-xs text-muted-foreground"
          autoEdit={pendingAccess === 'limited' && !note}
        />
      )}
      {noteError && (
        <Text size="xs" className="text-destructive">
          {noteError}
        </Text>
      )}
    </div>
  );
}
